(function () {
  const ACCEPT_NAME = /\.(pdf|png|jpe?g|gif|webp|heic|heif|bmp)$/i;
  const MAX_BYTES = 20 * 1024 * 1024;
  const config = window.BellaPrescriptionConfig || { strings: {}, blocked: false };

  const BellaPrescription = {
    get strings() {
      return config.strings || {};
    },

    init() {
      if (this.initialized) return;
      this.initialized = true;
      this.handleCache = {};
      this.bindUploads(document);
      this.bindCartGate(document);
      this.bindCheckoutGuards();
      this.bindProductFormCapture();
      this.syncCheckoutState();
      this.sweepUnverifiedFromConfig();
      this.bindCartSweepEvents();
      document.addEventListener('shopify:section:load', (event) => {
        this.bindUploads(event.target);
        this.bindCartGate(event.target);
        this.syncCheckoutState();
      });
      document.addEventListener('cart:updated', () => {
        this.bindCartGate(document);
        this.syncCheckoutState();
        this.scheduleCartSweep();
      });
      document.addEventListener('ajaxProduct:added', () => {
        this.bindUploads(document);
        this.bindCartGate(document);
        this.syncCheckoutState();
        this.scheduleCartSweep();
      });
    },

    bindProductFormCapture() {
      document.addEventListener(
        'click',
        (event) => {
          const button = event.target && event.target.closest('[data-prescription-submit]');
          if (!button || button.disabled) return;
          const form = button.form || button.closest('form');
          if (!form || form.getAttribute('data-type') !== 'add-to-cart-form') return;
          const productForm = button.closest('product-form');
          const sourceId = productForm && productForm.getAttribute('data-prescription-source');
          if (sourceId) {
            const sourceForm = document.getElementById(sourceId);
            const sourceBlock = sourceForm && sourceForm.querySelector('[data-prescription-upload]');
            if (!sourceForm || !sourceBlock) return;
            event.preventDefault();
            event.stopPropagation();
            if (typeof event.stopImmediatePropagation === 'function') event.stopImmediatePropagation();
            this.copyStickyFields(form, sourceForm);
            this.submitProductForm(sourceForm, sourceBlock);
            return;
          }
          const block = form.querySelector('[data-prescription-upload]');
          if (!block) return;
          event.preventDefault();
          event.stopPropagation();
          if (typeof event.stopImmediatePropagation === 'function') event.stopImmediatePropagation();
          this.submitProductForm(form, block);
        },
        true
      );

      document.addEventListener(
        'submit',
        (event) => {
          const form = event.target;
          if (!(form instanceof HTMLFormElement)) return;
          if (form.getAttribute('data-type') !== 'add-to-cart-form') return;
          const block = form.querySelector('[data-prescription-upload]');
          if (!block) return;

          event.preventDefault();
          event.stopPropagation();
          if (typeof event.stopImmediatePropagation === 'function') {
            event.stopImmediatePropagation();
          }

          this.submitProductForm(form, block);
        },
        true
      );

      document.addEventListener(
        'click',
        (event) => {
          const accelerated = event.target && event.target.closest('[data-prescription-accelerated]');
          if (!accelerated) return;
          const form = accelerated.closest('form');
          if (!form || form.getAttribute('data-type') !== 'add-to-cart-form') return;
          const block = form.querySelector('[data-prescription-upload]');
          if (!block) return;

          event.preventDefault();
          event.stopPropagation();
          if (typeof event.stopImmediatePropagation === 'function') event.stopImmediatePropagation();

          if (!this.validateUpload(block)) {
            block.scrollIntoView({ behavior: 'smooth', block: 'center' });
            return;
          }

          this.submitProductForm(form, block, '/checkout');
        },
        true
      );
    },

    copyStickyFields(stickyForm, sourceForm) {
      const sourceId = sourceForm.querySelector('[name="id"]');
      const stickyId = stickyForm.querySelector('[name="id"]');
      if (sourceId && stickyId) sourceId.value = stickyId.value;
      const sourceQty = sourceForm.querySelector('[name="quantity"]');
      const stickyQty = stickyForm.querySelector('[name="quantity"]');
      if (sourceQty && stickyQty) sourceQty.value = stickyQty.value;
    },

    submitProductForm(form, block, returnTo) {
      if (form.dataset.prescriptionSubmitting === 'true') return;
      if (!this.validateUpload(block)) {
        block.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }

      this.syncUploadState(block);
      form.dataset.prescriptionSubmitting = 'true';
      const submitButton = form.querySelector('[name="add"], [data-prescription-submit]');
      if (submitButton) submitButton.classList.add('loading');

      const idInput = form.querySelector('[name="id"]');
      if (idInput) idInput.disabled = false;

      const fileInput = block.querySelector('[data-prescription-file]');
      const hasFile = Boolean(fileInput && fileInput.files && fileInput.files.length);
      if (fileInput && !hasFile) {
        fileInput.disabled = true;
      }

      const cartUrl = (window.theme && theme.routes && theme.routes.cart_url) || '/cart';
      this.setReturnTo(form, returnTo || cartUrl);

      // Native POST to /cart/add so Shopify can store the file as a line item property.
      // The Ajax Cart API cannot attach uploaded files.
      form.submit();
    },

    setReturnTo(form, url) {
      let input = form.querySelector('[name="return_to"]');
      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'return_to';
        form.appendChild(input);
      }
      input.value = url;
    },

    bindUploads(root) {
      root.querySelectorAll('[data-prescription-upload]').forEach((block) => {
        if (block.dataset.bound === 'true') return;
        block.dataset.bound = 'true';
        const fileInput = block.querySelector('[data-prescription-file]');
        const emailLater = block.querySelector('[data-prescription-email-later]');
        const alreadyProvided = block.querySelector('[data-prescription-already-provided]');
        const filename = block.querySelector('[data-prescription-filename]');
        const placeholder = block.querySelector('[data-prescription-placeholder]');
        const dropzone = block.querySelector('[data-prescription-dropzone]');

        const applyFile = (file) => {
          this.clearError(block);
          if (file) this.clearExclusiveCheckboxes(block);
          if (file && !this.isAcceptedFile(file)) {
            if (fileInput) fileInput.value = '';
            this.showError(block, this.strings.fileTypeError);
            if (filename) {
              filename.hidden = true;
              filename.textContent = '';
            }
            if (placeholder) placeholder.hidden = false;
            this.syncUploadState(block);
            return;
          }
          if (filename) {
            if (file) {
              filename.hidden = false;
              const template = this.strings.fileSelected || 'Selected: [file]';
              filename.textContent = template.replace('[file]', file.name);
              if (placeholder) placeholder.hidden = true;
            } else {
              filename.hidden = true;
              filename.textContent = '';
              if (placeholder) placeholder.hidden = false;
            }
          }
          this.syncUploadState(block);
        };

        if (fileInput) {
          fileInput.addEventListener('change', () => applyFile(fileInput.files[0]));
        }

        if (dropzone && fileInput) {
          dropzone.addEventListener('keydown', (event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              fileInput.click();
            }
          });
          ['dragenter', 'dragover'].forEach((type) => {
            dropzone.addEventListener(type, (event) => {
              event.preventDefault();
              dropzone.classList.add('is-dragover');
            });
          });
          ['dragleave', 'drop'].forEach((type) => {
            dropzone.addEventListener(type, (event) => {
              event.preventDefault();
              dropzone.classList.remove('is-dragover');
            });
          });
          dropzone.addEventListener('drop', (event) => {
            const file = event.dataTransfer && event.dataTransfer.files && event.dataTransfer.files[0];
            if (!file) return;
            try {
              const transfer = new DataTransfer();
              transfer.items.add(file);
              fileInput.files = transfer.files;
            } catch (error) {
              console.error(error);
            }
            applyFile(file);
          });
        }

        const onCheckboxChange = (changed) => {
          this.clearError(block);
          this.applyExclusiveChoice(block, changed);
          this.syncUploadState(block);
        };

        if (emailLater) {
          emailLater.addEventListener('change', () => onCheckboxChange(emailLater));
        }
        if (alreadyProvided) {
          alreadyProvided.addEventListener('change', () => onCheckboxChange(alreadyProvided));
        }

        this.syncUploadState(block);
      });
    },

    bindCartGate(root) {
      root.querySelectorAll('[data-prescription-cart-gate]').forEach((gate) => {
        if (gate.dataset.bound === 'true') return;
        gate.dataset.bound = 'true';

        gate.querySelectorAll('[data-prescription-line-file]').forEach((input) => {
          input.addEventListener('change', () => {
            const line = input.closest('[data-prescription-line]');
            if (!line || !input.files[0]) return;
            if (!this.isAcceptedFile(input.files[0])) {
              input.value = '';
              this.showError(gate, this.strings.fileTypeError);
              return;
            }
            this.attachFileToLine(line, input.files[0], gate);
          });
        });

        gate.querySelectorAll('[data-prescription-line-email]').forEach((input) => {
          input.addEventListener('change', () => {
            if (!input.checked) return;
            const line = input.closest('[data-prescription-line]');
            const other = line && line.querySelector('[data-prescription-line-already]');
            if (other) other.checked = false;
            if (line) this.markEmailLater(line, gate);
          });
        });

        gate.querySelectorAll('[data-prescription-line-already]').forEach((input) => {
          input.addEventListener('change', () => {
            if (!input.checked) return;
            const line = input.closest('[data-prescription-line]');
            const other = line && line.querySelector('[data-prescription-line-email]');
            if (other) other.checked = false;
            if (line) this.markAlreadyProvided(line, gate);
          });
        });
      });
    },

    bindCheckoutGuards() {
      document.addEventListener(
        'submit',
        (event) => {
          const form = event.target;
          if (!(form instanceof HTMLFormElement)) return;
          if (form.getAttribute('data-type') === 'add-to-cart-form') return;
          const submitter = event.submitter;
          const isCheckout = submitter && submitter.getAttribute('name') === 'checkout';
          if (!isCheckout) return;
          if (this.hasUnverifiedLines()) {
            event.preventDefault();
            event.stopPropagation();
            this.focusGate();
          }
        },
        true
      );

      document.addEventListener(
        'click',
        (event) => {
          const checkoutControl = event.target.closest(
            'button[name="checkout"], input[name="checkout"], .additional-checkout-buttons, shopify-accelerated-checkout, shopify-accelerated-checkout-cart, a[href*="/checkout"]'
          );
          if (checkoutControl && this.hasUnverifiedLines()) {
            event.preventDefault();
            event.stopPropagation();
            this.focusGate();
          }
        },
        true
      );
    },

    onProductFormSubmit(productForm, evt) {
      const form = productForm.form || productForm.querySelector('form');
      if (!form) return true;
      if (evt.defaultPrevented) return false;

      const stickySourceId = productForm.getAttribute('data-prescription-source');
      if (stickySourceId) {
        const sourceForm = document.getElementById(stickySourceId);
        const sourceBlock = sourceForm && sourceForm.querySelector('[data-prescription-upload]');
        if (sourceForm && sourceBlock) {
          evt.preventDefault();
          this.copyStickyFields(form, sourceForm);
          this.submitProductForm(sourceForm, sourceBlock);
          return false;
        }
      }

      const block = form.querySelector('[data-prescription-upload]');
      if (!block) return true;
      evt.preventDefault();
      this.submitProductForm(form, block);
      return false;
    },

    isCheckboxOnly(block) {
      return Boolean(block && block.getAttribute('data-prescription-mode') === 'checkbox');
    },

    clearExclusiveCheckboxes(block) {
      const emailLater = block.querySelector('[data-prescription-email-later]');
      const alreadyProvided = block.querySelector('[data-prescription-already-provided]');
      if (emailLater) emailLater.checked = false;
      if (alreadyProvided) alreadyProvided.checked = false;
    },

    clearSelectedFile(block) {
      const fileInput = block.querySelector('[data-prescription-file]');
      const filename = block.querySelector('[data-prescription-filename]');
      const placeholder = block.querySelector('[data-prescription-placeholder]');
      if (fileInput) fileInput.value = '';
      if (filename) {
        filename.hidden = true;
        filename.textContent = '';
      }
      if (placeholder) placeholder.hidden = false;
    },

    applyExclusiveChoice(block, changed) {
      if (!changed || !changed.checked) return;
      const emailLater = block.querySelector('[data-prescription-email-later]');
      const alreadyProvided = block.querySelector('[data-prescription-already-provided]');
      if (changed === emailLater && alreadyProvided) alreadyProvided.checked = false;
      if (changed === alreadyProvided && emailLater) emailLater.checked = false;
      this.clearSelectedFile(block);
    },

    validateUpload(block) {
      const fileInput = block.querySelector('[data-prescription-file]');
      const emailLater = block.querySelector('[data-prescription-email-later]');
      const alreadyProvided = block.querySelector('[data-prescription-already-provided]');
      const hasFile = fileInput && fileInput.files && fileInput.files.length > 0;
      const hasEmailLater = emailLater && emailLater.checked;
      const hasAlreadyProvided = alreadyProvided && alreadyProvided.checked;

      if (this.isCheckboxOnly(block)) {
        if (!hasEmailLater && !hasAlreadyProvided) {
          this.showError(block, this.strings.checkboxRequiredError || this.strings.requiredError);
          return false;
        }
        this.clearError(block);
        return true;
      }

      if (hasFile && !this.isAcceptedFile(fileInput.files[0])) {
        this.showError(block, this.strings.fileTypeError);
        return false;
      }
      if (!hasFile && !hasEmailLater && !hasAlreadyProvided) {
        this.showError(block, this.strings.requiredError);
        return false;
      }
      this.clearError(block);
      return true;
    },

    syncUploadState(block) {
      const fileInput = block.querySelector('[data-prescription-file]');
      const emailLater = block.querySelector('[data-prescription-email-later]');
      const alreadyProvided = block.querySelector('[data-prescription-already-provided]');
      const status = block.querySelector('[data-prescription-status]');
      const label = block.querySelector('[data-prescription-label]');
      const hold = block.querySelector('[data-prescription-hold]');
      const attrHold = block.querySelector('[data-prescription-attr-hold]');
      const attrVerification = block.querySelector('[data-prescription-attr-verification]');
      const accelerated = block.closest('form') && block.closest('form').querySelector('[data-prescription-accelerated]');
      const hasEmailLater = Boolean(emailLater && emailLater.checked);
      const hasAlreadyProvided = Boolean(alreadyProvided && alreadyProvided.checked);
      const hasFile = Boolean(fileInput && fileInput.files && fileInput.files.length);
      const hasChoice = hasFile || hasEmailLater || hasAlreadyProvided;

      block.classList.toggle('is-email-later', hasEmailLater || hasAlreadyProvided);

      if (fileInput) {
        fileInput.removeAttribute('required');
        fileInput.disabled = false;
      }
      if (label) {
        label.disabled = !hasChoice;
        label.value = hasFile
          ? this.strings.labelUploaded || 'Uploaded ✓'
          : hasAlreadyProvided
            ? this.strings.labelAlreadyProvided || 'Already purchased/provided'
            : hasEmailLater
              ? this.strings.labelEmailLater || 'To be emailed after checkout'
              : '';
      }
      if (status) {
        status.disabled = false;
        status.value = hasAlreadyProvided
          ? this.strings.statusAlreadyProvided
          : hasEmailLater
            ? this.strings.statusEmailLater
            : hasFile
              ? this.strings.statusUploaded
              : '';
      }
      if (hold) {
        hold.disabled = false;
        if (!hasChoice) hold.value = this.strings.holdNote || hold.value;
      }
      if (attrHold) {
        attrHold.disabled = !hasChoice;
      }
      if (attrVerification) {
        attrVerification.value = hasAlreadyProvided
          ? this.strings.verificationAlreadyProvided
          : hasEmailLater
            ? this.strings.verificationEmailLater
            : hasFile
              ? this.strings.verificationUploaded
              : '';
        attrVerification.disabled = !attrVerification.value;
      }
      if (accelerated) {
        accelerated.hidden = false;
        accelerated.classList.remove('is-hidden');
        accelerated.classList.toggle('is-blocked', !hasChoice);
      }
    },

    isAcceptedFile(file) {
      if (!file) return false;
      if (file.size > MAX_BYTES) return false;
      if (file.type && (file.type.indexOf('image/') === 0 || file.type === 'application/pdf')) return true;
      return ACCEPT_NAME.test(file.name || '');
    },

    showError(root, message) {
      const error = root.querySelector('[data-prescription-error]');
      if (!error) {
        window.alert(message);
        return;
      }
      error.hidden = false;
      error.textContent = message;
    },

    clearError(root) {
      const error = root.querySelector('[data-prescription-error]');
      if (!error) return;
      error.hidden = true;
      error.textContent = '';
    },

    hasUnverifiedLines() {
      return Boolean(document.querySelector('[data-prescription-unverified="true"]'));
    },

    bindCartSweepEvents() {
      if (typeof subscribe === 'function' && window.PUB_SUB_EVENTS && PUB_SUB_EVENTS.cartUpdate) {
        subscribe(PUB_SUB_EVENTS.cartUpdate, () => this.scheduleCartSweep());
      }
    },

    sweepUnverifiedFromConfig() {
      const keys = (config.unverifiedKeys || []).filter(Boolean);
      if (!keys.length) return;
      this.removeUnverifiedKeys(keys);
    },

    scheduleCartSweep() {
      if (this.sweepTimer) window.clearTimeout(this.sweepTimer);
      this.sweepTimer = window.setTimeout(() => this.sweepFromCartAjax(), 250);
    },

    async sweepFromCartAjax() {
      if (this.sweeping || this.sweepFailed) return;
      const cartUrl = ((window.theme && theme.routes && theme.routes.cart_url) || '/cart') + '.js';
      try {
        const cart = await fetch(cartUrl, { headers: { Accept: 'application/json' } }).then((response) => response.json());
        const keys = [];
        for (let i = 0; i < (cart.items || []).length; i++) {
          const item = cart.items[i];
          const props = item.properties || {};
          if (this.hasPrescriptionEvidence(props)) continue;
          if (await this.isPrescriptionCartItem(item)) keys.push(item.key);
        }
        if (keys.length) await this.removeUnverifiedKeys(keys);
      } catch (error) {
        console.error(error);
      }
    },

    hasPrescriptionEvidence(props) {
      if (!props) return false;
      return Boolean(
        props['Prescription'] ||
          props['_Prescription file'] ||
          props['Prescription file'] ||
          props['_Email later'] ||
          props['Email later'] ||
          props['_Already provided'] ||
          props['Already provided']
      );
    },

    normalizeTagList(value) {
      return String(value || '')
        .toLowerCase()
        .split(',')
        .map((tag) => tag.trim().replace(/-/g, ' '))
        .filter(Boolean);
    },

    hasConfiguredTag(tags, configured) {
      return configured.some((needle) => tags.indexOf(needle) !== -1);
    },

    async isPrescriptionCartItem(item) {
      const handle = item.handle || '';
      if (!handle) return false;
      const known = config.prescriptionHandles || [];
      if (known.indexOf(handle) !== -1) return true;
      if (Object.prototype.hasOwnProperty.call(this.handleCache, handle)) return this.handleCache[handle];
      try {
        const root = (window.theme && theme.routes && theme.routes.root_url) || '/';
        const base = root.charAt(root.length - 1) === '/' ? root : root + '/';
        const product = await fetch(base + 'products/' + handle + '.js', { headers: { Accept: 'application/json' } }).then(
          (response) => response.json()
        );
        const tags = this.normalizeTagList(product.tags);
        const uploadTags = this.normalizeTagList(config.uploadTags || 'Prescription lenses');
        const planoTags = this.normalizeTagList(config.planoTags || 'Plano documents');
        const isRx = this.hasConfiguredTag(tags, uploadTags) || this.hasConfiguredTag(tags, planoTags);
        this.handleCache[handle] = isRx;
        return isRx;
      } catch (error) {
        this.handleCache[handle] = false;
        return false;
      }
    },

    async removeUnverifiedKeys(keys) {
      const uniqueKeys = [];
      keys.forEach((key) => {
        if (key && uniqueKeys.indexOf(key) === -1) uniqueKeys.push(key);
      });
      if (!uniqueKeys.length || this.sweeping) return;
      this.sweeping = true;
      try {
        for (let i = 0; i < uniqueKeys.length; i++) {
          const response = await fetch(theme.routes.cart_change_url, {
            method: 'POST',
            headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: uniqueKeys[i], quantity: 0 })
          });
          const result = await response.json();
          if (result.status) throw new Error(result.description || result.message);
        }
        try {
          sessionStorage.setItem('bellaPrescriptionRemoved', '1');
        } catch (error) {}
        window.location.reload();
      } catch (error) {
        this.sweepFailed = true;
        this.sweeping = false;
        console.error(error);
      }
    },

    syncCheckoutState() {
      const blocked = this.hasUnverifiedLines();
      document.body.classList.toggle('prescription-checkout-blocked', blocked);
      document.querySelectorAll('[data-prescription-cart-gate]').forEach((gate) => {
        gate.hidden = !blocked;
      });
      document.querySelectorAll('[name="checkout"]').forEach((button) => {
        if (blocked) {
          button.setAttribute('disabled', 'disabled');
          button.setAttribute('aria-disabled', 'true');
        } else if (button.closest('form') && !button.closest('.is-empty')) {
          button.removeAttribute('disabled');
          button.removeAttribute('aria-disabled');
        }
      });
      document.querySelectorAll('.additional-checkout-buttons').forEach((el) => {
        el.hidden = blocked;
      });
    },

    focusGate() {
      const gate = document.querySelector('[data-prescription-cart-gate], [data-prescription-upload]');
      if (gate) gate.scrollIntoView({ behavior: 'smooth', block: 'center' });
      const host = gate || document.body;
      const checkboxOnly = this.isCheckboxOnly(host);
      const hasUploadLine = Boolean(
        host && host.querySelector('[data-prescription-mode="upload"], [data-prescription-file], [data-prescription-line-file]')
      );
      const message =
        checkboxOnly || (host && host.querySelector('[data-prescription-mode="checkbox"]') && !hasUploadLine)
          ? this.strings.checkboxCheckoutBlocked || this.strings.checkoutBlocked
          : this.strings.checkoutBlocked;
      this.showError(host, message);
    },

    async attachFileToLine(line, file, gate) {
      this.clearError(gate);
      gate.classList.add('loading');
      try {
        await fetch(theme.routes.cart_change_url, {
          method: 'POST',
          headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: line.dataset.key, quantity: 0 })
        });
        await this.updateCartAttributes(this.strings.verificationUploaded);
        this.submitNativeAdd(
          {
            id: line.dataset.variantId,
            quantity: line.dataset.quantity || '1',
            'properties[Prescription]': this.strings.labelUploaded || 'Uploaded ✓',
            'properties[_Prescription status]': this.strings.statusUploaded,
            'properties[_Fulfillment hold]': this.strings.holdNote,
            'attributes[_Prescription verification]': this.strings.verificationUploaded,
            'attributes[_Fulfillment hold]': this.strings.holdNote
          },
          { name: 'properties[_Prescription file]', file: file },
          line.dataset.sellingPlan
        );
      } catch (error) {
        this.showError(gate, error.message || this.strings.requiredError);
        gate.classList.remove('loading');
      }
    },

    submitNativeAdd(fields, fileField, sellingPlan) {
      const form = document.createElement('form');
      form.method = 'post';
      form.action = (window.theme && theme.routes && theme.routes.cart_add_url) || '/cart/add';
      form.enctype = 'multipart/form-data';
      form.hidden = true;

      Object.keys(fields).forEach((name) => {
        if (fields[name] == null || fields[name] === '') return;
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        input.value = fields[name];
        form.appendChild(input);
      });

      if (sellingPlan) {
        const plan = document.createElement('input');
        plan.type = 'hidden';
        plan.name = 'selling_plan';
        plan.value = sellingPlan;
        form.appendChild(plan);
      }

      if (fileField && fileField.file) {
        const input = document.createElement('input');
        input.type = 'file';
        input.name = fileField.name;
        try {
          const transfer = new DataTransfer();
          transfer.items.add(fileField.file);
          input.files = transfer.files;
        } catch (error) {
          console.error(error);
        }
        form.appendChild(input);
      }

      this.setReturnTo(form, (window.theme && theme.routes && theme.routes.cart_url) || '/cart');
      document.body.appendChild(form);
      form.submit();
    },

    async markAlreadyProvided(line, gate) {
      this.clearError(gate);
      gate.classList.add('loading');
      try {
        await fetch(theme.routes.cart_change_url, {
          method: 'POST',
          headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: line.dataset.key,
            quantity: Number(line.dataset.quantity || 1),
            properties: {
              Prescription: this.strings.labelAlreadyProvided || 'Already purchased/provided',
              '_Already provided': this.strings.alreadyProvidedValue,
              '_Prescription status': this.strings.statusAlreadyProvided,
              '_Fulfillment hold': this.strings.holdNote
            }
          })
        });
        await this.updateCartAttributes(this.strings.verificationAlreadyProvided);
        window.location.reload();
      } catch (error) {
        this.showError(gate, error.message || this.strings.requiredError);
        gate.classList.remove('loading');
      }
    },

    async markEmailLater(line, gate) {
      this.clearError(gate);
      gate.classList.add('loading');
      try {
        await fetch(theme.routes.cart_change_url, {
          method: 'POST',
          headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: line.dataset.key,
            quantity: Number(line.dataset.quantity || 1),
            properties: {
              Prescription: this.strings.labelEmailLater || 'To be emailed after checkout',
              '_Email later':
                line.dataset.prescriptionMode === 'checkbox'
                  ? this.strings.planoEmailLaterValue || this.strings.emailLaterValue
                  : this.strings.emailLaterValue,
              '_Prescription status': this.strings.statusEmailLater,
              '_Fulfillment hold': this.strings.holdNote
            }
          })
        });
        await this.updateCartAttributes(this.strings.verificationEmailLater);
        window.location.reload();
      } catch (error) {
        this.showError(gate, error.message || this.strings.requiredError);
        gate.classList.remove('loading');
      }
    },

    async updateCartAttributes(verification) {
      await fetch(theme.routes.cart_update_url, {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify({
          attributes: {
            '_Prescription verification': verification,
            '_Fulfillment hold': this.strings.holdNote
          }
        })
      });
    },

    async afterProductAdd(form) {
      const block = form && form.querySelector('[data-prescription-upload]');
      if (!block) return;
      const emailLater = block.querySelector('[data-prescription-email-later]');
      const alreadyProvided = block.querySelector('[data-prescription-already-provided]');
      const verification = alreadyProvided && alreadyProvided.checked
        ? this.strings.verificationAlreadyProvided
        : emailLater && emailLater.checked
          ? this.strings.verificationEmailLater
          : this.strings.verificationUploaded;
      try {
        await this.updateCartAttributes(verification);
      } catch (error) {
        console.error(error);
      }
    }
  };

  window.BellaPrescription = BellaPrescription;
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => BellaPrescription.init());
  } else {
    BellaPrescription.init();
  }
})();
